import { buildGoalOverviewReorderUpdate } from '@griply/utils';
import {
  useCreateUnlimitedGoalsAllowed,
  useSortedGoals,
  useUpdateGoal,
} from 'features/goals';
import { useLocalizedLifeAreas, useUpdateLifeArea } from 'features/life-areas';
import { useUpdateUserSettings } from 'features/user';
import { useCallback } from 'react';
import { useUser } from 'shared/hooks/use-user';
import { GoalReorderSectionUpdate, UpdateGoal } from 'shared/types/goal';
import { GoalGroupBy } from 'shared/types/sorting-mode';

export const useGoalTreeReorder = (groupBy: GoalGroupBy) => {
  const { submit: updateGoal } = useUpdateGoal();
  const { submit: updateLifeArea } = useUpdateLifeArea();
  const updateSettings = useUpdateUserSettings();
  const sortedGoals = useSortedGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const user = useUser();

  // needed for de-archiving logic
  const { allowed } = useCreateUnlimitedGoalsAllowed();

  return useCallback(
    (
      updatedSections: GoalReorderSectionUpdate[],
      updatedGoals: UpdateGoal[],
    ) => {
      const { goalUpdates, lifeAreaUpdates, settingsUpdate } =
        buildGoalOverviewReorderUpdate({
          updatedSections,
          updatedGoals,
          goals: sortedGoals,
          lifeAreas,
          groupBy,
          allowed,
          currentGoalGroupSorting:
            user?.settings?.goalGroupSorting?.lifeArea?.sorting,
        });

      updateSettings(settingsUpdate);
      lifeAreaUpdates.forEach((lifeArea) => updateLifeArea(lifeArea));
      goalUpdates.forEach((goal) => updateGoal(goal));
    },
    [
      groupBy,
      sortedGoals,
      lifeAreas,
      user?.settings?.goalGroupSorting?.lifeArea?.sorting,
      allowed,
      updateSettings,
      updateLifeArea,
      updateGoal,
    ],
  );
};
